import { useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText } from "lucide-react";
import { RichTextEditor } from "@/components/mc/RichTextEditor";
import { fmtEvWhen } from "@/components/mc/ui";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const pdfHref = (u) => (u && String(u).startsWith("http") ? u : API_BASE + u);

const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales: { "en-US": enUS } });
const DnDCalendar = withDragAndDrop(Calendar);

const STATUS_COLORS = {
  requested: "#f59e0b", contract_sent: "#6366f1", signed: "#0ea5e9",
  confirmed: "#16a34a", completed: "#64748b", cancelled: "#cbd5e1",
};
const STATUS_LABEL = {
  requested: "⏳ Awaiting your review", contract_sent: "📤 Contract sent — waiting on signature",
  signed: "✅ Signed — ready to confirm", confirmed: "🔒 Booked", completed: "✔ Completed", cancelled: "✕ Cancelled",
};

export function EventsCalendar({ bookings, settings, saveSettings, reload }) {
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [detail, setDetail] = useState(null);

  const durationMin = 120;
  const events = useMemo(() => {
    const evs = [];
    (bookings || []).filter((b) => b.status !== "cancelled").forEach((b) => {
      if (!b.date || !b.time) return;
      const start = new Date(`${b.date}T${b.time}`);
      evs.push({ id: b.id, title: `${b.first_name || ""} · ${b.package_name || "Event"}`, start, end: new Date(start.getTime() + durationMin * 60000), booking: b, kind: "booking" });
    });
    (settings.blocked_dates || []).forEach((d) => {
      const s = new Date(`${d}T00:00:00`);
      evs.push({ id: `bd-${d}`, title: "Blocked (all day)", start: s, end: s, allDay: true, kind: "blocked_date", key: d });
    });
    (settings.blocked_slots || []).forEach((k) => {
      const s = new Date(k);
      if (isNaN(s)) return;
      evs.push({ id: `bs-${k}`, title: "Blocked", start: s, end: new Date(s.getTime() + (settings.slot_interval_min || 30) * 60000), kind: "blocked_slot", key: k });
    });
    return evs;
  }, [bookings, settings, durationMin]);

  const eventStyle = (event) => {
    if (event.kind === "blocked_date" || event.kind === "blocked_slot")
      return { style: { backgroundColor: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: 4, backgroundImage: "repeating-linear-gradient(45deg,#fecaca,#fecaca 4px,#fee2e2 4px,#fee2e2 8px)" } };
    return { style: { backgroundColor: STATUS_COLORS[event.booking.status] || "#64748b", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 } };
  };

  const onSelectEvent = (event) => {
    if (event.kind === "blocked_date") { if (window.confirm("Unblock this date?")) toggleDate(event.key); return; }
    if (event.kind === "blocked_slot") { if (window.confirm("Unblock this time slot?")) toggleSlot(event.key); return; }
    setDetail(event.booking);
  };

  const onSelectSlot = ({ start, slots, action }) => {
    if (view === "month") {
      // toggle each selected day
      const days = (slots && slots.length ? slots : [start]).map((d) => format(d, "yyyy-MM-dd"));
      const set = new Set(settings.blocked_dates || []);
      const anyUnblocked = days.some((d) => !set.has(d));
      days.forEach((d) => (anyUnblocked ? set.add(d) : set.delete(d)));
      saveSettings({ blocked_dates: [...set] });
      toast.success(anyUnblocked ? "Date(s) blocked" : "Date(s) unblocked");
    } else {
      const key = format(start, "yyyy-MM-dd'T'HH:mm");
      toggleSlot(key);
    }
  };
  const toggleDate = (d) => {
    const set = new Set(settings.blocked_dates || []); set.has(d) ? set.delete(d) : set.add(d);
    saveSettings({ blocked_dates: [...set] });
  };
  const toggleSlot = (k) => {
    const set = new Set(settings.blocked_slots || []); const had = set.has(k); had ? set.delete(k) : set.add(k);
    saveSettings({ blocked_slots: [...set] }); toast.success(had ? "Slot unblocked" : "Slot blocked");
  };

  const min = new Date(); min.setHours(...(settings.slot_start || "08:00").split(":").map(Number), 0, 0);
  const max = new Date(); max.setHours(...(settings.slot_end || "18:00").split(":").map(Number), 0, 0);

  const onEventDrop = async ({ event, start }) => {
    if (event.kind !== "booking") return;
    const b = event.booking;
    const newDate = format(start, "yyyy-MM-dd");
    const newTime = view === "month" ? b.time : format(start, "HH:mm");
    if (newDate === b.date && newTime === b.time) return;
    try {
      await opApi.put(`/tenant/bookings/${b.id}/reschedule`, { date: newDate, time: newTime });
      toast.success(`Moved to ${newDate} at ${newTime} — customer emailed`);
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="events-calendar">
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        {Object.entries(STATUS_COLORS).map(([k, c]) => (
          <span key={k} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm" style={{ background: c }} />{k}</span>
        ))}
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm border border-red-300" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fecaca,#fecaca 3px,#fee2e2 3px,#fee2e2 6px)" }} />blocked</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Click a booking to manage it, or <b>drag it</b> to reschedule (the customer is emailed the new time). In <b>Month</b> view, click/drag empty days to block/unblock them; in <b>Week/Day</b> view, click a time slot to block/unblock it.</p>
      <div className="border border-border bg-white" style={{ height: 660, padding: 8 }}>
        <DnDCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={["month", "week", "day", "agenda"]}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          selectable
          popup
          resizable={false}
          draggableAccessor={(e) => e.kind === "booking"}
          onEventDrop={onEventDrop}
          min={min}
          max={max}
          step={settings.slot_interval_min || 30}
          timeslots={1}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          eventPropGetter={eventStyle}
          style={{ height: "100%" }}
        />
      </div>
      {detail && <BookingDetailDialog booking={detail} onClose={() => setDetail(null)} reload={reload} />}
    </div>
  );
}

export function BookingDetailDialog({ booking, onClose, reload }) {
  const b = booking;
  const [mode, setMode] = useState("view");
  const [quote, setQuote] = useState("");
  const [cForm, setCForm] = useState({ quote_amount: "", deposit_amount: "", terms: "" });
  const meta = STATUS_LABEL[b.status] || b.status;
  const sel = Object.entries(b.selection_names || b.selections || {}).filter(([, v]) => v && v.length);

  const setStatus = async (status) => {
    try {
      const { data } = await opApi.put(`/tenant/bookings/${b.id}/status`, { status, quote_amount: quote ? parseFloat(quote) : undefined });
      toast.success(status === "confirmed" ? "Booked — confirmation email sent" : "Updated");
      if (data && data.warning) toast.warning(data.warning, { duration: 8000 });
      onClose(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const sendContract = async () => {
    try {
      await opApi.post(`/tenant/bookings/${b.id}/contract`, {
        quote_amount: cForm.quote_amount ? parseFloat(cForm.quote_amount) : undefined,
        deposit_amount: cForm.deposit_amount ? parseFloat(cForm.deposit_amount) : undefined,
        terms: cForm.terms || undefined,
      });
      toast.success("Contract emailed to customer"); onClose(); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none" data-testid="calendar-booking-detail">
        <DialogHeader>
          <DialogTitle>{b.first_name} {b.last_name}</DialogTitle>
          <DialogDescription>{fmtEvWhen(b.date, b.time, b.timezone)} · {b.package_name}</DialogDescription>
        </DialogHeader>
        {mode === "view" ? (
          <div className="space-y-2 text-sm">
            <div><span className="text-xs font-semibold px-2 py-1 bg-secondary">{meta}</span></div>
            <div className="text-muted-foreground">{b.phone} · {b.email}</div>
            {b.event_address && <div className="text-muted-foreground">📍 {b.event_address}</div>}
            <div className="text-muted-foreground">{b.guest_count || "—"} guests · {b.event_type || "—"}</div>
            {sel.length > 0 && <div className="border-t border-border pt-2 text-xs text-muted-foreground">{sel.map(([k, v]) => <div key={k}><b className="text-foreground capitalize">{k}:</b> {v.join(", ")}</div>)}</div>}
            {b.notes && <div className="text-xs text-muted-foreground">Note: {b.notes}</div>}
            <div className="text-sm">Est. ${Number(b.estimated_total || 0).toFixed(2)}{b.quote_amount != null && <span className="ml-2 font-semibold text-primary">Quoted ${Number(b.quote_amount).toFixed(2)}</span>}</div>
            {b.contract_pdf_url && <a href={pdfHref(b.contract_pdf_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="cal-contract-pdf"><FileText className="h-3.5 w-3.5" /> View signed contract</a>}

            {(b.status === "requested" && b.contract_required) && (
              <div className="flex gap-2 pt-2">
                <Button size="sm" className="rounded-none" onClick={() => setMode("contract")} data-testid="cal-send-contract">Send contract</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus("cancelled")} data-testid="cal-decline">Decline</Button>
              </div>
            )}
            {((b.status === "requested" && !b.contract_required) || b.status === "signed") && (
              <div className="flex flex-wrap gap-2 items-center pt-2">
                <Input className="rounded-none h-9 w-28 text-sm" placeholder="Quote $" value={quote} onChange={(e) => setQuote(e.target.value)} data-testid="cal-quote" />
                <Button size="sm" className="rounded-none" onClick={() => setStatus("confirmed")} data-testid="cal-confirm">Confirm &amp; book</Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setStatus("cancelled")} data-testid="cal-decline">Decline</Button>
              </div>
            )}
            {b.status === "contract_sent" && (
              <div className="flex gap-2 pt-2">
                <span className="text-xs text-muted-foreground self-center">Waiting on signature.</span>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setMode("contract")} data-testid="cal-resend">Resend contract</Button>
                <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => setStatus("cancelled")} data-testid="cal-cancel">Cancel</Button>
              </div>
            )}
            {b.status === "confirmed" && <Button size="sm" variant="outline" className="rounded-none mt-2" onClick={() => setStatus("completed")} data-testid="cal-complete">Mark completed</Button>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quote ($)</Label><Input type="number" className="rounded-none mt-1" value={cForm.quote_amount} onChange={(e) => setCForm({ ...cForm, quote_amount: e.target.value })} data-testid="cal-contract-quote" /></div>
              <div><Label>Deposit ($)</Label><Input type="number" className="rounded-none mt-1" value={cForm.deposit_amount} onChange={(e) => setCForm({ ...cForm, deposit_amount: e.target.value })} /></div>
            </div>
            <div><Label>Terms (blank = your default)</Label><RichTextEditor value={cForm.terms} onChange={(html) => setCForm({ ...cForm, terms: html })} testid="cal-contract-terms" /></div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setMode("view")}>Back</Button>
              <Button className="rounded-none" onClick={sendContract} data-testid="cal-email-contract">Email contract</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
